import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../store";
import { PricingConfiguration } from "../../../types/pricingConfigurationTypes";
import { toast } from "react-toastify";
import { IAuthResponse } from "../auth/IAuthState";
import { createPricingConfiguration, fetchPricingConfiguration, updatePricingConfiguration } from "./pricingConfigurationAsyncThunk";
import { RequestResults } from "../../common/RequestStatus";

export interface DeletePricingConfiguration extends IAuthResponse {
  id: string;
}

interface PricingConfigurationState {
  data: PricingConfiguration;
  status: RequestResults;
  loading: RequestResults;
  error: string | null;
}

const initialState: PricingConfigurationState = {
  data: {} as PricingConfiguration,
  status: RequestResults.idle,
  loading: RequestResults.idle,
  error: null,
};

// Slice
const pricingConfigurationSlice = createSlice({
  name: "pricingConfiguration",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchPricingConfiguration.pending, (state) => {
        state.status = RequestResults.pending;
        state.error = null;
      })
      .addCase(fetchPricingConfiguration.fulfilled, (state, action: PayloadAction<PricingConfiguration>) => {
        state.status = RequestResults.success;
        state.data = action.payload;
      })
      .addCase(fetchPricingConfiguration.rejected, (state, action) => {
        state.status = RequestResults.failed;
        toast.error(action.payload || "Failed to fetch pricing configuration");
        state.error = action.payload || "Failed to fetch pricing configuration";
      });
    builder
      .addCase(createPricingConfiguration.pending, (state) => {
        state.loading = RequestResults.pending;
      })
      .addCase(createPricingConfiguration.fulfilled, (state, action: PayloadAction<PricingConfiguration>) => {
        state.loading = RequestResults.success;
        state.data = action.payload;
      })
      .addCase(createPricingConfiguration.rejected, (state, action) => {
        state.loading = RequestResults.failed;
        toast.error(action.payload || "Failed to add pricing configuration");
        state.error = action.payload || "Failed to add pricing configuration";
      });
    builder
      .addCase(updatePricingConfiguration.pending, (state) => {
        state.loading = RequestResults.pending;
      })
      .addCase(updatePricingConfiguration.fulfilled, (state, action: PayloadAction<PricingConfiguration>) => {
        state.data = action.payload;
        state.loading = RequestResults.success;
      })
      .addCase(updatePricingConfiguration.rejected, (state, action) => {
        toast.error(action.payload || "Failed to update pricing configuration");
        state.error = action.payload || "Failed to update pricing configuration";
        state.loading = RequestResults.failed;
      });
  },
});

export const selectPricingConfigurations = (state: RootState) => state.pricingConfiguration;

export default pricingConfigurationSlice.reducer;
